import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Moon, Calendar, ChevronRight, Loader2, Play, Pause, Volume2, Share2 } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import ShareCard from "@/components/ShareCard";
import CycleIntelligenceCard from "@/components/dashboard/CycleIntelligenceCard";
import CycleInsightsBanner from "@/components/dashboard/CycleInsightsBanner";
import WhatsNewModal from "@/components/dashboard/WhatsNewModal";
import PregnancyDashboardSection from "@/components/dashboard/PregnancyDashboardSection";
import TTCDashboardSection from "@/components/dashboard/TTCDashboardSection";
import MeditationStreakCard from "@/components/dashboard/MeditationStreakCard";
import QuickActionsGrid from "@/components/dashboard/QuickActionsGrid";
import GetMobileAppBanner from "@/components/dashboard/GetMobileAppBanner";

const phaseColors = {
  menstrual: "from-cycle-menstrual/20 to-cycle-menstrual/5 border-cycle-menstrual/30",
  follicular: "from-cycle-follicular/20 to-cycle-follicular/5 border-cycle-follicular/30",
  ovulation: "from-cycle-ovulation/20 to-cycle-ovulation/5 border-cycle-ovulation/30",
  luteal: "from-cycle-luteal/20 to-cycle-luteal/5 border-cycle-luteal/30",
  unknown: "from-mystic-700/20 to-mystic-800/5 border-mystic-600/30",
};

const Dashboard = () => {
  const { user, token } = useAuth();
  const [moonData, setMoonData] = useState(null);
  const [cycleData, setCycleData] = useState(null);
  const [cycleIntelligence, setCycleIntelligence] = useState(null);
  const [wellnessMode, setWellnessMode] = useState(null);
  const [recommendedMeditation, setRecommendedMeditation] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingAudio, setPlayingAudio] = useState(false);
  const [completingMeditation, setCompletingMeditation] = useState(false);
  const [meditationStartTime, setMeditationStartTime] = useState(null);
  const [showShareCard, setShowShareCard] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [moonRes, cycleRes, meditationRes, streakRes, predictionRes, analysisRes, modeRes] = await Promise.all([
          axios.get(`${API}/moon/current`),
          axios.get(`${API}/cycle/current`, { headers, withCredentials: true }),
          axios.get(`${API}/meditations/recommended`, { headers, withCredentials: true }).catch(() => null),
          token ? axios.get(`${API}/meditations/streak`, { headers, withCredentials: true }).catch(() => null) : null,
          axios.get(`${API}/period/prediction`, { headers, withCredentials: true }).catch(() => null),
          axios.get(`${API}/period/analysis`, { headers, withCredentials: true }).catch(() => null),
          axios.get(`${API}/wellness/mode`, { headers, withCredentials: true }).catch(() => null),
        ]);

        setMoonData(moonRes.data);
        setCycleData(cycleRes.data);
        if (meditationRes?.data?.meditations?.length > 0) {
          setRecommendedMeditation(meditationRes.data);
        }
        if (streakRes?.data) {
          setStreakData(streakRes.data);
        }
        if (predictionRes?.data) {
          setCycleIntelligence({
            ...predictionRes.data,
            regularity: analysisRes?.data?.cycle_regularity,
          });
        }
        if (modeRes?.data) {
          setWellnessMode(modeRes.data);
        }
      } catch (error) {
        // dashboard displays fallback data
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const completeMeditation = async (meditation) => {
    if (!token || completingMeditation) return;

    setCompletingMeditation(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const duration = meditationStartTime ? Math.floor((Date.now() - meditationStartTime) / 1000) : 60;

      const res = await axios.post(`${API}/meditations/complete`, {
        meditation_id: meditation.id,
        duration_seconds: duration,
        moon_phase: recommendedMeditation?.context?.moon_phase,
        cycle_phase: recommendedMeditation?.context?.cycle_phase,
      }, { headers, withCredentials: true });

      if (res.data) {
        setStreakData((prev) => ({
          ...prev,
          current_streak: res.data.current_streak,
          longest_streak: res.data.longest_streak,
          total_sessions: res.data.total_sessions,
          meditated_today: true,
        }));
      }
    } catch (error) {
      // dashboard displays fallback data
    } finally {
      setCompletingMeditation(false);
    }
  };

  const toggleAudio = (meditation) => {
    if (playingAudio) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudio(false);
      if (meditationStartTime && token) {
        completeMeditation(meditation);
      }
      setMeditationStartTime(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(meditation.audio_url);
      audio.play();
      audio.onended = () => {
        setPlayingAudio(false);
        audioRef.current = null;
        if (token) {
          completeMeditation(meditation);
        }
        setMeditationStartTime(null);
      };
      audioRef.current = audio;
      setPlayingAudio(true);
      setMeditationStartTime(Date.now());
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      <WhatsNewModal />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Welcome, <span className="gradient-text">{user?.name || "Goddess"}</span>
        </h1>
        <p className="text-mystic-200/70">Here's your cosmic alignment for today</p>
        <Button onClick={() => setShowShareCard(true)} variant="outline" size="sm" className="mt-3 border-mystic-500/30 text-mystic-200 hover:text-white rounded-full" data-testid="share-cosmic-btn">
          <Share2 className="w-4 h-4 mr-1" /> Share My Cosmic Card
        </Button>
      </motion.div>

      {showShareCard && <ShareCard onClose={() => setShowShareCard(false)} />}

      <GetMobileAppBanner />

      <CycleInsightsBanner />

      {/* Main Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
          <Card className="glass border-moon-gold/20 h-full" data-testid="moon-phase-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Moon className="w-5 h-5 text-moon-gold" />
                Current Moon
              </CardTitle>
              <Link to="/moon">
                <Button variant="ghost" size="sm" className="text-mystic-200 hover:text-white">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-4">
              {moonData && (
                <>
                  <div className="text-center py-4">
                    <span className="text-6xl" role="img" aria-label={moonData.phase_name}>
                      {moonData.phase_emoji}
                    </span>
                    <h3 className="text-2xl font-semibold text-white mt-4">{moonData.phase_name}</h3>
                    <p className="text-moon-gold">{moonData.illumination}% illuminated</p>
                  </div>
                  <div className="glass-light rounded-xl p-4">
                    <p className="text-sm text-mystic-100 font-medium mb-1">Moon Energy</p>
                    <p className="text-mystic-200/80">{typeof moonData.energy === "object" ? moonData.energy.energy : moonData.energy}</p>
                  </div>
                  <div className="flex justify-between text-sm text-mystic-200/70">
                    <span>Next New Moon: {moonData.days_to_new_moon} days</span>
                    <span>Next Full Moon: {moonData.days_to_full_moon} days</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
          <Card className={`glass border h-full bg-gradient-to-br ${phaseColors[cycleData?.phase || "unknown"]}`} data-testid="cycle-phase-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cycle-ovulation" />
                Your Cycle
              </CardTitle>
              <Link to="/calendar">
                <Button variant="ghost" size="sm" className="text-mystic-200 hover:text-white">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-4">
              {cycleData && (
                <>
                  <div className="text-center py-4">
                    <div className="w-20 h-20 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-4">
                      <span className="text-3xl font-bold text-white">
                        {cycleData.cycle_day || "?"}
                      </span>
                    </div>
                    <h3 className="text-2xl font-semibold text-white">{cycleData.phase_name}</h3>
                    <p className="text-mystic-200/80">{cycleData.description}</p>
                  </div>
                  <div className="glass-light rounded-xl p-4">
                    <p className="text-sm text-mystic-100 font-medium mb-1">Current Energy</p>
                    <p className="text-mystic-200/80">{cycleData.energy}</p>
                  </div>
                  {cycleData.days_until_next_period && (
                    <p className="text-center text-mystic-200/70">
                      Next period in ~{cycleData.days_until_next_period} days
                    </p>
                  )}
                  {cycleData.message && (
                    <p className="text-center text-mystic-300">{cycleData.message}</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {cycleIntelligence && <CycleIntelligenceCard prediction={cycleIntelligence} />}

      {/* Mode-specific section */}
      {wellnessMode?.mode === "pregnancy" && (
        <PregnancyDashboardSection wellnessMode={wellnessMode} />
      )}
      {wellnessMode?.mode === "ttc" && cycleIntelligence && (
        <TTCDashboardSection cycleIntelligence={cycleIntelligence} />
      )}

      <QuickActionsGrid />

      {token && <MeditationStreakCard streakData={streakData} />}

      {/* Today's Recommendations */}
      {cycleData?.remedies_summary && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card className="glass" data-testid="recommendations-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white">
                Today's Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {cycleData.remedies_summary.top_herb && (
                  <div className="glass-light rounded-xl p-4">
                    <p className="text-sm text-mystic-300 uppercase tracking-wide mb-2">Top Herb</p>
                    <p className="text-white font-medium">{cycleData.remedies_summary.top_herb.name}</p>
                    <p className="text-mystic-200/70 text-sm">{cycleData.remedies_summary.top_herb.benefit}</p>
                  </div>
                )}
                {cycleData.remedies_summary.top_crystal && (
                  <div className="glass-light rounded-xl p-4">
                    <p className="text-sm text-mystic-300 uppercase tracking-wide mb-2">Crystal Ally</p>
                    <p className="text-white font-medium">{cycleData.remedies_summary.top_crystal.name}</p>
                    <p className="text-mystic-200/70 text-sm">{cycleData.remedies_summary.top_crystal.benefit}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Today's Recommended Meditation */}
      {recommendedMeditation && recommendedMeditation.meditations?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Card className="glass bg-gradient-to-br from-indigo-500/10 to-purple-500/10" data-testid="recommended-meditation-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-indigo-400" />
                  Today's Meditation
                </span>
                <Link to="/rituals">
                  <Button variant="ghost" size="sm" className="text-mystic-300 hover:text-white">
                    See all <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardTitle>
              {recommendedMeditation.context && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="text-xs px-2 py-1 glass-light rounded-full text-mystic-300">
                    🌙 {recommendedMeditation.context.moon_phase}
                  </span>
                  <span className="text-xs px-2 py-1 glass-light rounded-full text-mystic-300">
                    ✨ Moon in {recommendedMeditation.context.moon_sign}
                  </span>
                  {recommendedMeditation.context.cycle_phase !== "unknown" && (
                    <span className="text-xs px-2 py-1 glass-light rounded-full text-mystic-300">
                      🌸 {recommendedMeditation.context.cycle_phase} phase
                    </span>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="glass-light rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    recommendedMeditation.meditations[0].category === "moon" ? "bg-moon-gold/20 text-moon-gold" :
                    recommendedMeditation.meditations[0].category === "cycle" ? "bg-pink-500/20 text-pink-300" :
                    recommendedMeditation.meditations[0].category === "healing" ? "bg-green-500/20 text-green-300" :
                    "bg-purple-500/20 text-purple-300"
                  }`}>
                    {recommendedMeditation.meditations[0].phase}
                  </span>
                  <span className="text-mystic-400 text-xs">{recommendedMeditation.meditations[0].duration}</span>
                </div>
                <h4 className="text-white font-medium text-lg mb-1">{recommendedMeditation.meditations[0].title}</h4>
                <p className="text-mystic-200/60 text-sm mb-4">{recommendedMeditation.meditations[0].description}</p>
                <Button
                  onClick={() => toggleAudio(recommendedMeditation.meditations[0])}
                  className={`w-full ${
                    playingAudio
                      ? "bg-indigo-600 hover:bg-indigo-500"
                      : "bg-mystic-600 hover:bg-mystic-500"
                  } text-white rounded-full`}
                  data-testid="play-recommended-meditation-btn"
                >
                  {playingAudio ? (
                    <>
                      <Pause className="w-4 h-4 mr-2" />
                      Pause Meditation
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Start Meditation
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;
